import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Space, message, Modal, Spin, Empty } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import leaveService from '../services/leaveService';

const ActionPendingTab = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  useEffect(() => {
    fetchPendingLeaves(1, 10);
  }, []);

  const fetchPendingLeaves = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const response = await leaveService.getPendingForManager(page, limit);
      if (response.success) {
        setLeaves(response.data || []);
        setPagination({
          current: response.pagination?.page || page,
          pageSize: response.pagination?.limit || limit,
          total: response.pagination?.total || 0
        });
      }
    } catch (error) {
      console.error('Error fetching pending leaves:', error);
      message.error('Failed to fetch pending leaves');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (record) => {
    Modal.confirm({
      title: 'Approve Leave',
      icon: <CheckCircleOutlined />,
      content: `Approve leave for ${record.employee?.firstName} ${record.employee?.lastName} from ${new Date(record.startDate).toLocaleDateString()} to ${new Date(record.endDate).toLocaleDateString()}?`,
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'No',
      onOk: async () => {
        try {
          setLoading(true);
          await leaveService.approve(record.id, 'Approved by manager');
          message.success('Leave approved successfully');
          fetchPendingLeaves(pagination.current, pagination.pageSize);
        } catch (error) {
          message.error(error?.message || 'Failed to approve leave');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleReject = (record) => {
    Modal.confirm({
      title: 'Reject Leave',
      icon: <CloseCircleOutlined />,
      content: `Reject leave for ${record.employee?.firstName} ${record.employee?.lastName} from ${new Date(record.startDate).toLocaleDateString()} to ${new Date(record.endDate).toLocaleDateString()}?`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          setLoading(true);
          await leaveService.reject(record.id, 'Rejected by manager');
          message.success('Leave rejected successfully');
          fetchPendingLeaves(pagination.current, pagination.pageSize);
        } catch (error) {
          message.error(error?.message || 'Failed to reject leave');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'orange';
      case 'Approved': return 'green';
      case 'Rejected': return 'red';
      default: return 'blue';
    }
  };

  const columns = [
    {
      title: 'Employee Name',
      dataIndex: 'employee',
      key: 'employee',
      width: 200,
      render: (employee) => {
        return employee ? `${employee.firstName} ${employee.lastName}` : '-';
      }
    },
    {
      title: 'Leave Type',
      dataIndex: 'leaveType',
      key: 'leaveType',
      render: (leaveType) => leaveType?.name || '-'
    },
    {
      title: 'From Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date) => new Date(date).toLocaleDateString(),
      width: 120
    },
    {
      title: 'To Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date) => new Date(date).toLocaleDateString(),
      width: 120
    },
    {
      title: 'Days',
      dataIndex: 'numberOfDays',
      key: 'days',
      width: 80,
      align: 'center'
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (reason) => reason || '-'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        record.status === 'Pending' ? (
          <Space size="small">
            <Button
              type="primary"
              size="small"
              onClick={() => handleApprove(record)}
              loading={loading}
            >
              Approve
            </Button>
            <Button
              danger
              size="small"
              onClick={() => handleReject(record)}
              loading={loading}
            >
              Reject
            </Button>
          </Space>
        ) : (
          <Tag color={getStatusColor(record.status)}>{record.status}</Tag>
        )
      )
    }
  ];

  const handleTableChange = (paginationData) => {
    fetchPendingLeaves(paginationData.current, paginationData.pageSize);
  };

  if (leaves.length === 0 && !loading) {
    return <Empty description="No pending leaves for approval" />;
  }

  return (
    <Spin spinning={loading}>
      <Table
        columns={columns}
        dataSource={leaves}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['5', '10', '20', '50']
        }}
        onChange={handleTableChange}
        rowKey="id"
        scroll={{ x: 1200 }}
      />
    </Spin>
  );
};

export default ActionPendingTab;
